import React, { useState, useMemo } from 'react'
import { getFeed, actOnFeedItem, togglePin, FeedItem } from '../lib/api'
import { useApi } from '../hooks/useApi'
import FeedCard from '../components/FeedCard'
import ContextChat from '../components/ContextChat'
import PullToRefresh from '../components/PullToRefresh'

const FILTERS = [
  { label: 'All', value: '' },
  { label: 'Flagged', value: 'flagged' },
  { label: 'Actions', value: 'action_item' },
  { label: 'Email', value: 'email_intelligence' },
  { label: 'Stale', value: 'stale_alert' },
  { label: 'Meetings', value: 'meeting_summary' },
  { label: 'Contacts', value: 'contact_alert' },
]

export default function FeedPage() {
  const [filter, setFilter] = useState('')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [dismissed, setDismissed] = useState<Set<number>>(new Set())
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set())

  const params = filter === 'flagged'
    ? { pinned_only: 'true' }
    : filter
      ? { item_type: filter }
      : undefined

  const { data, loading, refresh } = useApi<FeedItem[]>(
    () => getFeed(params),
    [filter]
  )

  const visibleItems = data ? data.filter(item => !dismissed.has(item.id)) : []
  const allChecked = visibleItems.length > 0 && visibleItems.every(i => checkedIds.has(i.id))

  const selectedItem = selectedId && data
    ? data.find(i => i.id === selectedId)
    : null

  const selectedItemsForChat = useMemo(() => {
    if (checkedIds.size === 0) return undefined
    return Array.from(checkedIds).map(id => {
      const item = data?.find(f => f.id === id)
      return { type: 'feed_item', id, label: item?.headline.slice(0, 80) || `Feed #${id}` }
    })
  }, [checkedIds, data])

  function toggleCheck(id: number) {
    setCheckedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleCheckAll() {
    if (allChecked) {
      setCheckedIds(new Set())
    } else {
      setCheckedIds(new Set(visibleItems.map(i => i.id)))
    }
  }

  function clearChecked() {
    setCheckedIds(new Set())
  }

  async function bulkAction(action: string) {
    const ids = Array.from(checkedIds)
    await Promise.all(ids.map(id => actOnFeedItem(id, action)))
    if (action === 'dismissed') {
      setDismissed(prev => {
        const next = new Set(prev)
        ids.forEach(id => next.add(id))
        return next
      })
    }
    setCheckedIds(new Set())
    refresh()
  }

  async function bulkFlag() {
    await Promise.all(Array.from(checkedIds).map(id => togglePin(id)))
    setCheckedIds(new Set())
    refresh()
  }

  return (
    <div style={{ paddingBottom: 120 }}>
      <PullToRefresh onRefresh={refresh} />

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, overflowX: 'auto' }}>
        {FILTERS.map((f) => (
          <button
            key={f.value}
            className={`nav-tab ${filter === f.value ? 'active' : ''}`}
            style={{ padding: '6px 12px', fontSize: 13, borderRadius: 6 }}
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Bulk action bar */}
      {checkedIds.size > 0 && (
        <div className="bulk-bar">
          <label className="bulk-select-all">
            <input type="checkbox" checked={allChecked} onChange={toggleCheckAll} />
            <span>{checkedIds.size} selected</span>
          </label>
          <div className="bulk-actions">
            <button onClick={() => bulkAction('acknowledged')}>Got it</button>
            <button onClick={() => bulkAction('responded')}>Respond</button>
            <button onClick={bulkFlag}>Flag</button>
            <button className="danger" onClick={() => bulkAction('dismissed')}>Dismiss</button>
            <button className="muted" onClick={clearChecked}>Clear</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="loading">Loading feed...</div>
      ) : !data || visibleItems.length === 0 ? (
        <div className="empty-state">
          <div className="message">No items{filter ? ' for this filter' : ''}.</div>
        </div>
      ) : (
        visibleItems.map((item) => (
          <div key={item.id} className="feed-row">
            <input
              type="checkbox"
              className="feed-checkbox"
              checked={checkedIds.has(item.id)}
              onChange={() => toggleCheck(item.id)}
              onClick={e => e.stopPropagation()}
            />
            <div
              className={`feed-row-content${selectedId === item.id ? ' selected' : ''}`}
              onClick={() => setSelectedId(prev => prev === item.id ? null : item.id)}
            >
              <FeedCard
                item={item}
                onAction={refresh}
                onDismiss={(id) => { setDismissed(prev => new Set(prev).add(id)); refresh() }}
              />
            </div>
          </div>
        ))
      )}

      <ContextChat
        view="feed"
        selectedItem={selectedItem ? {
          type: 'feed_item',
          id: selectedItem.id,
          label: selectedItem.headline.slice(0, 40),
        } : null}
        selectedItems={selectedItemsForChat}
      />
    </div>
  )
}
